import { useRef, useState } from 'react';
import { setCrossFieldNavContext, openCrossFieldNav } from '../utils/crossFieldNavBus';
import { devLog } from '../utils/devLogger';

export interface FieldLocation {
  sectionId: string;
  sectionName: string;
  fieldName: string;
  fieldLabel: string;
}

/**
 * Cross-field navigation hook (matches 1.0 cross-field-navigation.js)
 * Detects fields with same names across different sections
 * 
 * ✅ SAFE: Pure computation + event bus (NO state updates, NO re-renders)
 * React components render buttons based on duplicateFields state
 */
export const useCrossFieldNavigation = () => {
  const fieldMapRef = useRef<Map<string, FieldLocation[]>>(new Map());
  const [duplicateFields, setDuplicateFields] = useState<Set<string>>(new Set());
  
  const scanFields = () => {
    const fieldMap = new Map<string, FieldLocation[]>();
    
    // ✅ PURE SCAN: Just read DOM to build location map (NO manipulation)
    document.querySelectorAll('.financial-input[name]').forEach(input => {
      const fieldName = input.getAttribute('name');
      const sectionElement = input.closest('.sub-content-item');
      const sectionId = sectionElement?.id;
      
      if (!fieldName || !sectionId) return;
      
      // Get section name from tab
      const tabButton = document.querySelector(`.btn-sub-panel[data-tab="${sectionId}"]`);
      const sectionName = tabButton?.textContent?.trim().replace(/\s*\d+$/, '') || sectionId;
      
      // Get field label
      const row = input.closest('tr');
      const labelCell = row?.querySelector('td:first-child');
      const fieldLabel = labelCell?.textContent?.trim() || fieldName;
      
      const location: FieldLocation = {
        sectionId,
        sectionName,
        fieldName,
        fieldLabel: fieldLabel.replace('See details', '').trim() // Remove existing nav text
      };
      
      if (!fieldMap.has(fieldName)) {
        fieldMap.set(fieldName, []);
      }
      
      // Only add if not already in this section
      const existingInSection = fieldMap.get(fieldName)!.find(loc => loc.sectionId === sectionId);
      if (!existingInSection) {
        fieldMap.get(fieldName)!.push(location);
      }
    });
    
    // Find duplicates
    const duplicates = new Set<string>();
    fieldMap.forEach((locations, fieldName) => {
      if (locations.length > 1) {
        duplicates.add(fieldName);
      }
    });
    
    fieldMapRef.current = fieldMap;
    setDuplicateFields(duplicates); // ✅ React will re-render tables with buttons
    
    devLog(`✅ Cross-field nav: Found ${duplicates.size} fields with multiple locations`);
  };

  /**
   * Show navigation menu for a field
   * Called by React-rendered button onClick
   * ✅ SAFE: Event bus pattern (NO state, NO re-renders)
   */
  const showNavigationMenu = (
    fieldName: string,
    sectionId: string,
    triggerButton: HTMLElement
  ) => {
    // Get all locations for this field
    const allLocations = fieldMapRef.current.get(fieldName) || [];
    if (allLocations.length <= 1) return;
    
    const currentLocation = allLocations.find(loc => loc.sectionId === sectionId);
    if (!currentLocation) return;
    
    // ✅ EVENT BUS: Set context and dispatch open event
    setCrossFieldNavContext({
      fieldName,
      currentLocation,
      allLocations,
      triggerButtonRect: triggerButton.getBoundingClientRect(),
      onNavigate: navigateToField
    });
    
    // Dispatch after a tick to avoid immediate close from click propagation
    setTimeout(() => openCrossFieldNav(), 0);
  };
  
  const navigateToField = (targetSectionId: string, fieldName: string) => {
    // Try sidebar tab first
    const tabButton = document.querySelector(`.btn-sub-panel[data-tab="${targetSectionId}"]`) as HTMLElement;
    
    if (tabButton) {
      // Sidebar mode: click the tab
      tabButton.click();
    } else {
      // PDF mode: dispatch event to avoid dropdown flicker
      document.dispatchEvent(new CustomEvent('crossFieldNavigate', {
        detail: { sectionId: targetSectionId }
      }));
    }
      
    // Wait for tab to activate, then highlight
    setTimeout(() => {
      const targetSection = document.getElementById(targetSectionId);
      const targetInput = targetSection?.querySelector(`.financial-input[name="${fieldName}"]`) as HTMLElement;
      
      if (targetInput) {
        // Scroll to field
        targetInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Highlight row
        const row = targetInput.closest('tr');
        if (row) {
          row.classList.add('cross-field-highlight');
          targetInput.classList.add('cross-field-highlight-input');
          
          // Remove highlight after 6 seconds
          setTimeout(() => {
            row.classList.remove('cross-field-highlight');
            targetInput.classList.remove('cross-field-highlight-input');
          }, 6000);
        }
        
        // Do not auto-focus after cross navigation.
        // Keep scroll + highlight only to avoid forcing caret/focus behavior.
      }
    }, 400);
  };
  
  const getOtherLocations = (fieldName: string, currentSectionId: string): FieldLocation[] => {
    const allLocations = fieldMapRef.current.get(fieldName) || [];
    return allLocations.filter(loc => loc.sectionId !== currentSectionId);
  };
  
  const getFieldLocations = (fieldName: string): FieldLocation[] => {
    return fieldMapRef.current.get(fieldName) || [];
  };
  
  return {
    duplicateFields,
    scanFields,
    navigateToField,
    getOtherLocations,
    getFieldLocations,
    showNavigationMenu
  };
};

